import { Calendar } from "primereact/calendar"
import { DateTime } from "luxon"
import { Accordion, AccordionTab } from "primereact/accordion"

// Custom components
import AppointmentsCalendarRecurringControllers from './AppointmentsCalendarRecurringControllers'
import AppointmentsCalendarAppointments from './AppointmentsCalendarAppointments'
import { calendarListTypes, getDate } from "./utils"

// Styles
import myStyles from "../../styles/appointments/Appointments.module.css"
import { Badge } from "primereact/badge"
import { Button } from "primereact/button"

export default function AppointmentsCalendarView({
  calSelectedDate,
  setCalSelectedDate,
  filterDateRange,
  handleDateRangeChanged,
  filteredAppointments,
  openEditModal,
  openDeleteConfirmation,
  openCancelConfirmation,
  loadingAppointmentsSmall,
  getOrgNameFromMap,
}) {

  // Custom date template for the calendar component.
  // Adds a visual indicator (a dot) to dates that have upcoming appointments.
  const calendarDateTemplate = (calDate) => {

    // If calDate is from a different month, return with special formatting.
    if (calDate.month !== filterDateRange[0]?.getMonth() || calDate.year !== filterDateRange[0]?.getFullYear()) {
      return (
        <div className={myStyles.calendarDateGreyedOut}>
          {calDate.day}
        </div>
      )
    }

    // Check if the current date has any upcoming appointments.
    const dateHasUpcoming = filteredAppointments?.upcoming?.some(d => {
      const apptDate = new Date(d.startDateTime);
      return (
        apptDate?.getDate() === calDate.day &&
        apptDate?.getMonth() === calDate.month &&
        apptDate?.getFullYear() === calDate.year
      )
    })

    // Check if the current date has any pending appointments.
    const dateHasPending = filteredAppointments?.pending?.some(d => {
      const apptDate = new Date(d.startDateTime);
      return (
        apptDate?.getDate() === calDate.day &&
        apptDate?.getMonth() === calDate.month &&
        apptDate?.getFullYear() === calDate.year &&
        apptDate?.getTime() > Date.now() // Only consider future pending appointments
      )
    })

    // Display day with custom style for upcoming appointments
    if (dateHasUpcoming) {
      return (
        <div className={myStyles.calendarDateTemplateUpcoming}>
          {calDate.day}
        </div>
      )
    }
    // Display day with custom style for pending appointments
    else if (dateHasPending) {
      return (
        <div className={myStyles.calendarDateTemplatePending}>
          {calDate.day}
        </div>
      )
    }

    return calDate.day; // Just display the day number.
  }

  function filterAppointmentsByDay(data, date) {
    const dayStart = DateTime.fromJSDate(date).toUnixInteger();
    const dayEnd = DateTime.fromJSDate(date).plus({ days: 1 }).toUnixInteger();
    return data.filter(x => {
      const startUnix = getDate(x.startDateTime).toUnixInteger();
      return startUnix > dayStart && startUnix < dayEnd;
    });
  }

  const getAccordionHeader = (title, count) => {
    return (
      <span className={myStyles.semiboldText}>
        {title}
        {count > 0 && <Badge className="ml-2" value={count} />}
      </span>
    )
  }

  const filteredUpcomingAppointments = filterAppointmentsByDay(filteredAppointments?.upcoming || [], calSelectedDate)
  const filteredPastAppointments = filterAppointmentsByDay(filteredAppointments?.past || [], calSelectedDate)
  const filteredPendingAppointments = filterAppointmentsByDay(filteredAppointments?.pending || [], calSelectedDate)
  const filteredDeclinedAppointments = filterAppointmentsByDay(filteredAppointments?.declined || [], calSelectedDate)

  return (
    <div className={myStyles.calendarViewContainer}>

      <div className={myStyles.calendarView}>
        <Calendar
          className={myStyles.calendar}
          inline
          value={calSelectedDate} // Currently selected date in calendar.
          viewDate={filterDateRange[0]} // Start date for the calendar view.
          onChange={(e) => {
            setCalSelectedDate(e.value) // Update selected date in calendar view.
          }}
          onViewDateChange={(e) => {
            const _filterDateStart = DateTime.fromJSDate(e.value).startOf('month').toJSDate()
            const _filterDateEnd = DateTime.fromJSDate(e.value).endOf('month').toJSDate()
            handleDateRangeChanged([_filterDateStart, _filterDateEnd])
          }}
          dateTemplate={calendarDateTemplate}
          disabled={loadingAppointmentsSmall} // Disable calendar interactions while loading.  
          footerTemplate={() => (
            <div className="flex justify-content-center mb-2">
              <Button
                label="Today"
                rounded
                className="pl-4 pr-4"
                onClick={() => {
                  const today = new Date();
                  const _filterDateStart = DateTime.fromJSDate(today).startOf('month').toJSDate()
                  const _filterDateEnd = DateTime.fromJSDate(today).endOf('month').toJSDate()

                  // Check if the selected date is already today by comparing year, month, and day
                  const isTodaySelected = calSelectedDate && DateTime.fromJSDate(calSelectedDate).hasSame(DateTime.fromJSDate(today), 'day');
                  if (isTodaySelected) {
                    return; // No need to update if already selected
                  }

                  // Check if today is within the current filter range
                  if (filterDateRange[0]?.getTime() !== _filterDateStart.getTime() ||
                    filterDateRange[1]?.getTime() !== _filterDateEnd.getTime()) {
                    handleDateRangeChanged([_filterDateStart, _filterDateEnd])
                  }

                  setCalSelectedDate(today);
                }}
                disabled={loadingAppointmentsSmall}
              />
            </div>
          )}
        />
        <div className={myStyles.calendarLegend}>
          <div className="flex flex-row gap-2 align-items-center">
            <div className={myStyles.calendarLegendToday} />
            <div className={myStyles.calendarLegendText}>
              Today
            </div>
          </div>
          <div className="flex flex-row gap-2 align-items-center">
            <div className={myStyles.calendarLegendSelected} />
            <div className={myStyles.calendarLegendText}>
              Selected Date
            </div>
          </div>
          <div className="flex flex-row gap-2 align-items-center">
            <div className={myStyles.calendarLegendUpcoming} />
            <div className={myStyles.calendarLegendText}>
              Upcoming Appointments
            </div>
          </div>
          <div className="flex flex-row gap-2 align-items-center">
            <div className={myStyles.calendarLegendPending} />
            <div className={myStyles.calendarLegendText}>
              Pending Requests
            </div>
          </div>
        </div>
      </div>

      <div className={myStyles.calendarViewRightPanel}>
        <Accordion activeIndex={0}>
          <AccordionTab header={getAccordionHeader("Upcoming Appointments", filteredUpcomingAppointments.length)}>
            <AppointmentsCalendarAppointments
              type={calendarListTypes.upcoming}
              filteredAppointments={filteredUpcomingAppointments}
              filteredAppointmentsALL={filteredAppointments}
              openEditModal={openEditModal}
              openCancelConfirmation={openCancelConfirmation}
              openDeleteConfirmation={openDeleteConfirmation}
              loadingAppointmentsSmall={loadingAppointmentsSmall}
              getOrgNameFromMap={getOrgNameFromMap}
            />
          </AccordionTab>
          <AccordionTab header={getAccordionHeader("Past Appointments", filteredPastAppointments.length)}>
            <AppointmentsCalendarAppointments
              type={calendarListTypes.past}
              filteredAppointments={filteredPastAppointments}
              openEditModal={openEditModal}
              openCancelConfirmation={openCancelConfirmation}
              openDeleteConfirmation={openDeleteConfirmation}
              loadingAppointmentsSmall={loadingAppointmentsSmall}
              getOrgNameFromMap={getOrgNameFromMap}
            />
          </AccordionTab>
          <AccordionTab header={getAccordionHeader("Pending Requests", filteredPendingAppointments.length)}>
            <AppointmentsCalendarAppointments
              type={calendarListTypes.pending}
              filteredAppointments={filteredPendingAppointments}
              openEditModal={openEditModal}
              openCancelConfirmation={openCancelConfirmation}
              openDeleteConfirmation={openDeleteConfirmation}
              loadingAppointmentsSmall={loadingAppointmentsSmall}
              getOrgNameFromMap={getOrgNameFromMap}
            />
          </AccordionTab>
          <AccordionTab header={getAccordionHeader("Declined Requests", filteredDeclinedAppointments.length)}>
            <AppointmentsCalendarAppointments
              type={calendarListTypes.declined}
              filteredAppointments={filteredDeclinedAppointments}
              openEditModal={openEditModal}
              openCancelConfirmation={openCancelConfirmation}
              openDeleteConfirmation={openDeleteConfirmation}
              loadingAppointmentsSmall={loadingAppointmentsSmall}
              getOrgNameFromMap={getOrgNameFromMap}
            />
          </AccordionTab>
          <AccordionTab header={getAccordionHeader("Recurring Controllers", filteredAppointments?.recurring?.length || 0)}>
            <AppointmentsCalendarRecurringControllers
              controllers={filteredAppointments?.recurring || []}
              onEditController={openEditModal}
              openCancelConfirmation={openCancelConfirmation}
              loadingAppointmentsSmall={loadingAppointmentsSmall}
              getOrgNameFromMap={getOrgNameFromMap}
            />
          </AccordionTab>
        </Accordion>

      </div>
    </div>
  )
}
